import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Record } from 'immutable';
import moment from 'moment';

import { PRODUCTS, PRODUCT_ROUTES } from '../../../constants';
import * as localise from '../../../utils/localise';

import SportIcon from '@xanadu/ui-shared/components/SportIcon';
import { MatchbookZeroFlag } from '../BetReportIcons/BetReportIcons';

import './MarketRow.scss';

const BASE = 'mb-reportMarketRow';

const Badge = ({ market, count, onToggle, openClass }) => (
  <a
    role="button"
    tabIndex={0}
    onClick={() => onToggle(market.id)}
    className={`${BASE}__toggle ${openClass}`}
  >
    {count}
  </a>
);

Badge.propTypes = {
  openClass: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  market: PropTypes.instanceOf(Record).isRequired,
  count: PropTypes.number.isRequired
};

const MarketRow = ({
  market,
  event,
  onToggle,
  count,
  currency,
  isOpen,
  type
}) => {
  let pnlClass = `${BASE}__pnl--`;
  let commissionClass = `${BASE}__commission--`;
  let potentialProfit = market.potentialProfit;
  let liability = market.liability || market.netProfitAndLoss;

  if (market.profitAndLoss >= 0.01) {
    pnlClass += 'positive';
  }

  if (type !== 'settled') {
    if (potentialProfit >= 0.01) {
      commissionClass += 'positive';
    } else if (potentialProfit < 0) {
      potentialProfit = Math.abs(potentialProfit);
      commissionClass += 'negative';
    }

    if (liability >= 0.01) {
      pnlClass += 'negative';
    } else if (liability < 0) {
      liability = Math.abs(liability);
      pnlClass += 'positive';
    }
  }

  const pnl = liability !== null ? localise.currency(liability, currency) : '-.--'; // pnl is liability for unmatched/matched reports
  let commission = potentialProfit !== null ? localise.currency(potentialProfit || 0, currency) : '-.--'; // commission is potentialProfit for unmatched/matched reports

  const productUrlPrefix = PRODUCT_ROUTES[market.product || PRODUCTS.exchange];

  if (type === 'settled') {
    commission = market.commission !== null ? localise.currency(market.commission, currency) : '-.--';
  }

  const name = (type === 'settled')
    ? event.name
    : (<Link className={`${BASE}__eventLink`} to={`${productUrlPrefix}/events/${event.id}`} >{event.name}</Link>);

  return (
    <tr
      className={BASE}
      role="button"
      tabIndex={0}
      onClick={() => onToggle(market.id)}
    >
      <td className={`${BASE}__column1`}>
        <Badge
          market={market}
          onToggle={onToggle}
          count={count}
          openClass={isOpen ? `${BASE}__toggle--open` : ''}
        />
        <SportIcon sport={event.sportUrl} className={`${BASE}__sportIcon`} />
      </td>
      <td className={`${BASE}__column2`}>
        <span className={`${BASE}__eventName`}>
          {name}
        </span>
        <span className={`${BASE}__startTime`}>
          {moment(event.startTime).format('DD MMM YYYY HH:mm')}
        </span>
      </td>
      <td>
        <span className={`${BASE}__marketName`}>
          {market.name}
          {type !== 'offers' && market.product === PRODUCTS.mbzero && (
            <span className={`${BASE}__ZeroFlag`}>
              {MatchbookZeroFlag}
            </span>
          )}
        </span>
        <span className={`${BASE}__settledTime`}>
          {market.settledTime ? moment(market.settledTime).format('DD MMM YYYY HH:mm') : ' '}
        </span>
      </td>
      <td />
      <td className={`${BASE}__stake`} />
      <td className={`${BASE}__pnl ${BASE}__pnl--${type} ${pnlClass} `}>
        {pnl}
      </td>
      <td className={`${BASE}__commission ${BASE}__commission--${type} ${commissionClass}`}>
        <span className={`${BASE}__commAmount`}>
          {commission}
        </span>
        <span className={`${BASE}__commType`} />
      </td>
    </tr>
  );
};

MarketRow.propTypes = {
  type: PropTypes.oneOf(['bets', 'offers', 'settled']).isRequired,
  currency: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  event: PropTypes.instanceOf(Record).isRequired,
  market: PropTypes.instanceOf(Record).isRequired,
  count: PropTypes.number,
  isOpen: PropTypes.bool
};

export default MarketRow;
